// In-memory seed data for standalone mode (VITE_USE_MOCKS=true). Shapes mirror the backend
// models (DATABASE_ENTITY_MAPPING.md) and the admin contracts (MISSING_APIS.md).
import { ROLES } from '@/constants/roles';

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const int = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const oid = () =>
  Array.from({ length: 24 }, () => '0123456789abcdef'[Math.floor(Math.random() * 16)]).join('');
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString();

const FIRST = ['Aarav', 'Vivaan', 'Aditya', 'Diya', 'Ananya', 'Ishaan', 'Riya', 'Kabir', 'Sara', 'Arjun', 'Meera', 'Kavya', 'Rohan', 'Neha', 'Vikram'];
const LAST = ['Sharma', 'Verma', 'Singh', 'Gupta', 'Yadav', 'Patel', 'Kumar', 'Mishra', 'Nair', 'Reddy'];
const CATS = ['Polity', 'Economy', 'Environment', 'Science & Tech', 'International Relations', 'History', 'Geography', 'Social Issues', 'Security', 'Governance', 'Ethics', 'Current Affairs'];
const CATS_HI = ['राजनीति', 'अर्थव्यवस्था', 'पर्यावरण', 'विज्ञान एवं तकनीक', 'अंतर्राष्ट्रीय संबंध', 'इतिहास', 'भूगोल', 'सामाजिक मुद्दे', 'सुरक्षा', 'शासन', 'नैतिकता', 'समसामयिकी'];

const name = () => `${pick(FIRST)} ${pick(LAST)}`;
const emailOf = (n, i) => `${n.toLowerCase().replace(/\s+/g, '.')}.${i}@example.com`;

// ── Users ──────────────────────────────────────────────────────────────────
const users = Array.from({ length: 64 }, (_, i) => {
  const n = name();
  const plan = Math.random() > 0.85 ? 'pro' : 'free';
  return {
    _id: oid(),
    name: n,
    email: emailOf(n, i),
    phone: null,
    plan,
    planExpiresAt: plan === 'pro' ? daysAgo(-int(-30, 200)) : null,
    examPreference: pick(['UPSC', 'UPPCS', 'BPSC']),
    authProvider: pick(['email', 'email', 'google', 'apple', 'facebook']),
    preferredLanguage: pick(['English', 'Hindi']),
    themeMode: pick(['dark', 'light']),
    isEmailVerified: Math.random() > 0.1,
    isProfileComplete: Math.random() > 0.2,
    status: Math.random() > 0.93 ? 'blocked' : 'active',
    streak: int(0, 45),
    articlesRead: int(0, 400),
    quizzesCompleted: int(0, 80),
    createdAt: daysAgo(int(0, 365)),
  };
});

// ── News ───────────────────────────────────────────────────────────────────
const makeMcq = (i) => ({
  question: `Sample question ${i + 1} on the topic?`,
  questionHi: `विषय पर नमूना प्रश्न ${i + 1}?`,
  options: ['Option A', 'Option B', 'Option C', 'Option D'],
  optionsHi: ['विकल्प A', 'विकल्प B', 'विकल्प C', 'विकल्प D'],
  correct: int(0, 3),
  explanation: 'Because the relevant provision/fact supports this option.',
  explanationHi: 'क्योंकि संबंधित तथ्य इस विकल्प का समर्थन करता है।',
});
const news = Array.from({ length: 42 }, (_, i) => {
  const ci = int(0, CATS.length - 1);
  const enriched = Math.random() > 0.18;
  const scope = pick(['national', 'national', 'international', 'state']);
  const state = scope === 'state' ? pick(['UP', 'Bihar']) : null;
  const exams = scope === 'state' ? (state === 'UP' ? ['UPPCS'] : ['BPSC']) : ['UPSC', 'UPPCS', 'BPSC'];
  return {
    _id: oid(),
    title: `Policy update #${i + 1}: ${CATS[ci]} reform announced`,
    titleHi: `नीति अद्यतन #${i + 1}: ${CATS_HI[ci]} सुधार की घोषणा`,
    category: CATS[ci],
    categoryHi: CATS_HI[ci],
    summary: 'A concise blurb summarising the development and its UPSC relevance.',
    scope,
    state,
    exams,
    sourceType: pick(['govt', 'newspaper', 'coaching', 'intl']),
    sourceId: pick(['pib', 'the-hindu', 'indian-express', 'drishti', 'rbi']),
    sourceUrl: `https://example.gov.in/article/${i + 1}`,
    source: pick(['PIB', 'The Hindu', 'Indian Express', 'RBI']),
    enriched,
    isActive: Math.random() > 0.08,
    mcqs: enriched ? Array.from({ length: 5 }, (_, k) => makeMcq(k)) : [],
    publishedAt: daysAgo(int(0, 60)),
    createdAt: daysAgo(int(0, 60)),
  };
});

// ── Payments ─────────────────────────────────────────────────────────────────
const PLAN_AMT = { monthly: 19900, quarterly: 49900, yearly: 149900 }; // paise, like /admin/payments
const payments = Array.from({ length: 36 }, () => {
  const u = pick(users);
  const planId = pick(['monthly', 'quarterly', 'yearly']);
  const status = pick(['paid', 'paid', 'paid', 'created', 'failed']);
  const coupon = Math.random() > 0.7 ? pick(['WELCOME50', 'UPSC100', 'NEWYEAR']) : null;
  const amount = PLAN_AMT[planId];
  return {
    _id: oid(),
    user: { _id: u._id, name: u.name, email: u.email },
    planId,
    durationDays: planId === 'monthly' ? 30 : planId === 'quarterly' ? 90 : 365,
    orderId: coupon === 'UPSC100' ? `free_${oid()}` : `order_${oid().slice(0, 14)}`,
    paymentId: status === 'paid' ? `pay_${oid().slice(0, 14)}` : null,
    amount: coupon === 'UPSC100' ? 100 : amount,
    currency: 'INR',
    status,
    couponCode: coupon,
    discountApplied: coupon ? int(5000, 50000) : 0,
    verifiedVia: status === 'paid' ? pick(['client', 'webhook']) : null,
    proValidUntil: status === 'paid' ? daysAgo(-int(0, 300)) : null,
    createdAt: daysAgo(int(0, 120)),
  };
});

// ── Coupons ──────────────────────────────────────────────────────────────────
const coupons = [
  { _id: oid(), code: 'WELCOME50', description: '50% off first purchase', discountType: 'percent', discountValue: 50, applicablePlans: [], maxRedemptions: 500, perUserLimit: 1, redemptionCount: 213, expiresAt: daysAgo(-90), isActive: true, createdAt: daysAgo(120) },
  { _id: oid(), code: 'UPSC100', description: 'Free Pro for scholarship students', discountType: 'percent', discountValue: 100, applicablePlans: ['monthly'], maxRedemptions: 100, perUserLimit: 1, redemptionCount: 41, expiresAt: daysAgo(-30), isActive: true, createdAt: daysAgo(60) },
  { _id: oid(), code: 'NEWYEAR', description: '₹200 off yearly', discountType: 'flat', discountValue: 200, applicablePlans: ['yearly'], maxRedemptions: null, perUserLimit: 1, redemptionCount: 88, expiresAt: null, isActive: true, createdAt: daysAgo(150) },
  { _id: oid(), code: 'EXPIRED20', description: 'Old promo', discountType: 'percent', discountValue: 20, applicablePlans: [], maxRedemptions: 200, perUserLimit: 1, redemptionCount: 200, expiresAt: daysAgo(15), isActive: false, createdAt: daysAgo(300) },
];

// ── Catalogues ─────────────────────────────────────────────────────────────
const categories = CATS.map((c, i) => ({
  _id: oid(), name: c, nameHi: CATS_HI[i], order: i + 1, isActive: true,
  articleCount: news.filter((n) => n.category === c).length, createdAt: daysAgo(200),
}));

const exams = [
  { _id: oid(), code: 'UPSC', name: 'UPSC Civil Services', nameHi: 'यूपीएससी सिविल सेवा', description: 'National & international current affairs', descriptionHi: 'राष्ट्रीय और अंतर्राष्ट्रीय समसामयिकी', order: 1, isActive: true, userCount: users.filter((u) => u.examPreference === 'UPSC').length, createdAt: daysAgo(300) },
  { _id: oid(), code: 'UPPCS', name: 'UPPCS (Uttar Pradesh)', nameHi: 'यूपीपीसीएस', description: 'UPSC feed + Uttar Pradesh affairs', descriptionHi: 'यूपीएससी फ़ीड + उत्तर प्रदेश', order: 2, isActive: true, userCount: users.filter((u) => u.examPreference === 'UPPCS').length, createdAt: daysAgo(300) },
  { _id: oid(), code: 'BPSC', name: 'BPSC (Bihar)', nameHi: 'बीपीएससी', description: 'UPSC feed + Bihar affairs', descriptionHi: 'यूपीएससी फ़ीड + बिहार', order: 3, isActive: true, userCount: users.filter((u) => u.examPreference === 'BPSC').length, createdAt: daysAgo(300) },
];

const alerts = [
  { _id: oid(), categoryTitle: 'UPSC Notifications', categoryTitleHi: 'यूपीएससी अधिसूचनाएँ', categoryIcon: 'gov', categoryColor: '#1E40AF', isActive: true, items: [{ title: 'CSE 2026 Prelims notification', titleHi: 'सीएसई 2026 प्रारंभिक अधिसूचना', date: '2026-02-14', dateHi: '14 फ़रवरी 2026', tag: 'New', tagHi: 'नया', tagColor: '#16A34A' }], createdAt: daysAgo(10) },
  { _id: oid(), categoryTitle: 'Admit Cards', categoryTitleHi: 'प्रवेश पत्र', categoryIcon: 'card', categoryColor: '#D97706', isActive: true, items: [{ title: 'Prelims admit card released', titleHi: 'प्रारंभिक प्रवेश पत्र जारी', date: '2026-05-20', dateHi: '20 मई 2026', tag: 'Live', tagHi: 'लाइव', tagColor: '#DC2626' }], createdAt: daysAgo(5) },
  { _id: oid(), categoryTitle: 'Results', categoryTitleHi: 'परिणाम', categoryIcon: 'result', categoryColor: '#16A34A', isActive: false, items: [], createdAt: daysAgo(40) },
];

const quizzes = Array.from({ length: 12 }, (_, i) => {
  const type = i < 9 ? 'daily' : 'weekly';
  const day = daysAgo(i);
  return {
    _id: oid(),
    type,
    dayKey: day.slice(0, 10),
    weekLabel: type === 'weekly' ? 'Jun 7–13' : undefined,
    questions: Array.from({ length: type === 'weekly' ? 15 : 10 }, (_, k) => makeMcq(k)),
    activeFrom: daysAgo(i + 1),
    activeTo: daysAgo(i - 1),
    attempts: int(20, 480),
    createdAt: day,
  };
});

const mains = Array.from({ length: 22 }, () => {
  const u = pick(users);
  const status = pick(['evaluated', 'evaluated', 'pending']);
  return {
    _id: oid(),
    userId: { _id: u._id, name: u.name, email: u.email },
    question: 'Critically examine the role of federalism in India\'s disaster management framework.',
    answerText: 'Federalism distributes responsibilities between the Union and States…',
    fileUrl: Math.random() > 0.6 ? 'https://example.com/mains/sheet.pdf' : null,
    score: status === 'evaluated' ? int(4, 10) : null,
    feedback: status === 'evaluated' ? 'Good structure; strengthen with more constitutional articles.' : null,
    pointsMissed: status === 'evaluated' ? ['Art. 355/356 nuance', 'NDMA 2005 specifics'] : [],
    valueAdditions: status === 'evaluated' ? ['Cite Sarkaria Commission'] : [],
    criteria: status === 'evaluated' ? [{ label: 'Content', labelHi: 'विषय-वस्तु', score: int(2, 5), total: 5 }, { label: 'Structure', labelHi: 'संरचना', score: int(2, 5), total: 5 }] : [],
    status,
    submittedAt: daysAgo(int(0, 30)),
  };
});

const chats = Array.from({ length: 24 }, () => {
  const u = pick(users);
  const count = int(2, 30);
  return {
    _id: oid(),
    userId: { _id: u._id, name: u.name, email: u.email },
    title: pick(['Explain Article 370', 'Difference between FDI and FII', 'Summarise the Budget 2026', 'What is the Fiscal Deficit?']),
    lastMessage: 'Thanks, that clarifies it!',
    messageCount: count,
    createdAt: daysAgo(int(1, 60)),
    updatedAt: daysAgo(int(0, 30)),
  };
});

const downloads = [
  { _id: oid(), title: 'NCERT Polity Notes', titleHi: 'एनसीईआरटी राजनीति नोट्स', fileType: 'PDF', fileSize: '2.4 MB', fileUrl: 'https://example.gov.in/notes/polity.pdf', colorHex: '#1E40AF', createdAt: daysAgo(50) },
  { _id: oid(), title: 'Economic Survey Summary', titleHi: 'आर्थिक सर्वेक्षण सारांश', fileType: 'PDF', fileSize: '5.1 MB', fileUrl: 'https://example.gov.in/notes/eco.pdf', colorHex: '#16A34A', createdAt: daysAgo(40) },
  { _id: oid(), title: 'Weekly Current Affairs', titleHi: 'साप्ताहिक समसामयिकी', fileType: 'News', fileSize: '1.2 MB', fileUrl: 'https://example.gov.in/notes/ca.pdf', colorHex: '#7C3AED', createdAt: daysAgo(7) },
];

const scraperSources = [
  { id: 'pib', name: 'Press Information Bureau', strategy: 'rss', status: 'ready', category: 'Current Affairs', region: null, runnable: true },
  { id: 'the-hindu', name: 'The Hindu', strategy: 'site', status: 'ready', category: 'Current Affairs', region: null, runnable: true },
  { id: 'indian-express', name: 'Indian Express', strategy: 'site', status: 'ready', category: 'Current Affairs', region: null, runnable: true },
  { id: 'rbi', name: 'Reserve Bank of India', strategy: 'rss', status: 'ready', category: 'Economy', region: null, runnable: true },
  { id: 'isro', name: 'ISRO', strategy: 'htmlList', status: 'ready', category: 'Science & Tech', region: null, runnable: true },
  { id: 'sebi', name: 'SEBI', strategy: 'pdfList', status: 'ready', category: 'Economy', region: null, runnable: true },
  { id: 'mea', name: 'Ministry of External Affairs', strategy: 'site', status: 'needs-config', category: 'International Relations', region: null, runnable: false },
  { id: 'ncrb', name: 'NCRB', strategy: 'pdfList', status: 'needs-config', category: 'Security', region: null, runnable: false },
  { id: 'amar-ujala-up', name: 'Amar Ujala (UP)', strategy: 'site', status: 'ready', category: 'State', region: 'UP', runnable: true },
  { id: 'prabhat-khabar-bihar', name: 'Prabhat Khabar (Bihar)', strategy: 'site', status: 'ready', category: 'State', region: 'Bihar', runnable: true },
];

const scraperRuns = Array.from({ length: 8 }, (_, i) => ({
  jobId: `job_${oid().slice(0, 10)}`,
  trigger: pick(['cron', 'manual']),
  startedAt: daysAgo(i),
  finishedAt: daysAgo(i),
  scraped: int(40, 180),
  persisted: int(20, 120),
  enriched: int(15, 110),
  errors: int(0, 6),
  status: 'done',
}));

const staff = [
  { _id: oid(), name: 'Owner Admin', email: 'owner@upsc.app', role: ROLES.SUPER_ADMIN, status: 'active', lastLoginAt: daysAgo(0) },
  { _id: oid(), name: 'Priya Admin', email: 'priya@upsc.app', role: ROLES.ADMIN, status: 'active', lastLoginAt: daysAgo(1) },
  { _id: oid(), name: 'Manish Manager', email: 'manish@upsc.app', role: ROLES.MANAGER, status: 'active', lastLoginAt: daysAgo(2) },
  { _id: oid(), name: 'Sunita Support', email: 'sunita@upsc.app', role: ROLES.SUPPORT, status: 'active', lastLoginAt: daysAgo(3) },
  { _id: oid(), name: 'Mohit Mod', email: 'mohit@upsc.app', role: ROLES.MODERATOR, status: 'inactive', lastLoginAt: daysAgo(20) },
];

const settings = {
  plans: { monthly: { price: 199 }, quarterly: { price: 499 }, yearly: { price: 1499 } },
  minOrderPaise: 100,
  currency: 'INR',
  scraper: { cron: '0 6,18 * * *', full: true, enrichLimit: 0, quiz: true },
  ai: { provider: 'local', geminiModel: 'gemini-2.5-flash', localModel: 'qwen2.5-7b-instruct', freeDailyLimit: 30 },
  rateLimits: { otpPerHour: 5, loginPer15m: 10, newsPerHour: 200 },
  storageDriver: 'local',
};

export const db = {
  users,
  news,
  payments,
  coupons,
  categories,
  exams,
  alerts,
  quizzes,
  mains,
  chats,
  downloads,
  scraperSources,
  scraperRuns,
  staff,
  settings,
};

export const helpers = { oid, int, pick, daysAgo, CATS, CATS_HI };
export default db;
